import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { LoadingScreen } from "Components";
import { unAuthService } from "Service/Axios";
import { getJobsFilterListUrl, getJobsListUrl } from "Constant/api";
import {
  useJobsPopularCategoryList,
  useJobsTestimonialList,
} from "Constant/apiHooks";
import { paths } from "Constant/paths";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { SPLIT_CHARACTER } from "Constant/constants";

const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 2000,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: { slidesToShow: 2 },
    },
    {
      breakpoint: 640,
      settings: { slidesToShow: 1 },
    },
  ],
};

export default function Careers() {
  const navigate = useNavigate();

  const categoryFilterDropdownRef = useRef(null);
  const advanceFilterDropdownRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState("");
  const [jobsFilterList, setJobsFilterList] = useState(null);
  const [totalJobsCount, setTotalJobsCount] = useState(0);
  const [jobsList, setJobsList] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filterData, setFilterData] = useState({
    search: "",
    location: "",
    category: [],
    filters: [],
  });

  const { jobsPopularCategoryList, loading: jobsPopularCategoryListLoading } =
    useJobsPopularCategoryList();
  const { jobsTestimonialList, loading: jobsTestimonialListLoading } =
    useJobsTestimonialList();

  // Fuction to toggle dropdowns
  const toggleDropdown = (type) => {
    setOpenDropdown((prev) => (prev === type ? "" : type));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryFilterDropdownRef.current &&
        !categoryFilterDropdownRef.current.contains(event.target) &&
        advanceFilterDropdownRef.current &&
        !advanceFilterDropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCategory = (e) => {
    const { checked, value } = e.target;
    setFilterData((prev) => {
      const updatedCategories = checked
        ? [...prev.category, value]
        : prev.category.filter((category) => category !== value);
      return { ...prev, category: updatedCategories };
    });
  };

  const handleChangeFilter = (e) => {
    const { checked, value } = e.target;
    setFilterData((prev) => {
      const updatedFilters = checked
        ? [...prev.filters, value]
        : prev.filters.filter((filter) => filter !== value);
      return { ...prev, filters: updatedFilters };
    });
  };

  const handleRemoveAppliedFilter = async (type, value) => {
    const currentFilters = { ...filterData };

    // Prepare updated filters
    switch (type) {
      case "search":
        currentFilters.search = "";
        break;

      case "location":
        currentFilters.location = "";
        break;

      case "category":
        currentFilters.category = currentFilters.category.filter(
          (c) => !c.endsWith(`${SPLIT_CHARACTER}${value}`)
        );
        break;

      case "filters":
        currentFilters.filters = currentFilters.filters.filter(
          (f) => !f.endsWith(`${SPLIT_CHARACTER}${value}`)
        );
        break;

      default:
        break;
    }

    const payload = getFilterPayload(currentFilters);
    await fetchJobsList(payload, currentFilters);

    setFilterData(currentFilters);
  };

  // Function to fetch list
  const fetchJobsFilterList = async () => {
    setFilterLoading(true);
    const payload = {};
    const response = await unAuthService(getJobsFilterListUrl, "POST", payload);
    if (!response || response.success === false) {
      setJobsFilterList(null);
      setFilterLoading(false);
      return;
    }
    setJobsFilterList(response);
    setFilterLoading(false);
  };

  const fetchJobsList = async (filterPayload, updatedAppliedFilters) => {
    setLoading(true);
    const payload = { ...filterPayload };
    const response = await unAuthService(getJobsListUrl, "POST", payload);
    if (!response || !Array.isArray(response.data)) {
      setJobsList([]);
      setLoading(false);
      return;
    }
    if (updatedAppliedFilters) {
      getBadgesFromFilterData(updatedAppliedFilters);
    }
    setJobsList(response.data);
    setTotalJobsCount(response.total || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchJobsFilterList();
    fetchJobsList();
  }, []);

  const getBadgesFromFilterData = (data) => {
    const badges = [];

    if (data.search) {
      badges.push({ type: "search", label: data.search });
    }

    if (data.location) {
      badges.push({ type: "location", label: data.location });
    }

    data.category.forEach((item) => {
      const [key, value] = item.split(SPLIT_CHARACTER);
      badges.push({
        type: "category",
        label: key,
        value,
      });
    });

    data.filters.forEach((item) => {
      const [, value] = item.split(SPLIT_CHARACTER);
      badges.push({
        type: "filters",
        label: value,
        value: value,
      });
    });

    setAppliedFilters(badges);
  };

  const getFilterPayload = (data) => {
    const structuredFilters = data.filters?.reduce((acc, filter) => {
      const [key, value] = filter.split(SPLIT_CHARACTER);
      const existing = acc.find((item) => item.filterType === key);
      if (existing) {
        existing.name.push(value);
      } else {
        acc.push({ filterType: key, name: [value] });
      }
      return acc;
    }, []);

    const structuredCategories = data.category?.map((cat) => {
      const [, value] = cat.split(SPLIT_CHARACTER);
      return value;
    });

    const payload = {
      search: data.search || undefined,
      location: data.location || undefined,
      category:
        structuredCategories && structuredCategories.length > 0
          ? structuredCategories
          : undefined,
      filters:
        structuredFilters && structuredFilters.length > 0
          ? structuredFilters
          : undefined,
    };

    return payload;
  };

  const onResetFilterJobs = async () => {
    const resetFilters = {
      search: "",
      location: "",
      category: [],
      filters: [],
    };
    const payload = getFilterPayload(resetFilters);
    await fetchJobsList(payload, resetFilters);
    setFilterData(resetFilters);
  };

  const onFilterJobs = async () => {
    const payload = getFilterPayload(filterData);
    await fetchJobsList(payload, filterData);
    navigate(paths.careers);
  };

  return (
    <main>
      <section className="py-lg-10 py-5 bg-primary-50">
        <div className="container-xl">
          <div className="search-bar position-relative">
            <div className="search-items d-flex align-items-center flex-grow-1">
              <i className="bx bx-search bx-sm text-primary-800"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Job title, Keyword..."
                name="search"
                value={filterData.search}
                onChange={handleChange}
              />
            </div>

            <div className="search-items d-flex align-items-center flex-grow-1">
              <i className="bx bx-map bx-sm text-primary-800"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Location"
                name="location"
                value={filterData.location}
                onChange={handleChange}
              />
            </div>

            <div
              className="search-items dropdown flex-grow-1 position-relative"
              id="categoryFilter"
              ref={categoryFilterDropdownRef}
            >
              <button
                className={`btn btn-white border-0 dropdown-toggle d-flex justify-content-between align-items-center w-100 ${
                  openDropdown === "categoryFilter" ? "show" : ""
                }`}
                type="button"
                aria-expanded={openDropdown === "categoryFilter"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDropdown("categoryFilter");
                }}
              >
                <div className="d-flex align-items-center gap-2 w-100">
                  <i className="bx bx-layer bx-sm text-primary-800"></i> Select
                  Category
                </div>
              </button>
              <ul
                className={`dropdown-menu p-4 w-100 top-100 max-h-350 overflow-y-auto shadow ${
                  openDropdown === "categoryFilter" ? "show" : ""
                }`}
              >
                {jobsPopularCategoryListLoading ? (
                  <li className="d-flex justify-content-center align-items-center p-4">
                    <LoadingScreen />
                  </li>
                ) : jobsPopularCategoryList &&
                  jobsPopularCategoryList.length > 0 ? (
                  jobsPopularCategoryList.map((category, index) => (
                    <li
                      className="form-check ps-4"
                      key={`${category}-${index}`}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="category"
                        id={category.gid}
                        value={`${category.title}${SPLIT_CHARACTER}${category.gid}`}
                        checked={filterData.category.includes(
                          `${category.title}${SPLIT_CHARACTER}${category.gid}`
                        )}
                        onChange={handleChangeCategory}
                      />
                      <label
                        className="form-check-label word-break"
                        htmlFor={category.gid}
                      >
                        {category.title}
                      </label>
                    </li>
                  ))
                ) : (
                  <li className="d-flex justify-content-center align-items-center p-4">
                    No Data Found
                  </li>
                )}
              </ul>
            </div>

            <div
              className="search-items dropdown position-static"
              id="advanceFilter"
              ref={advanceFilterDropdownRef}
            >
              <button
                className={`border-0 bg-transparent dropdown-toggle d-flex justify-content-between align-items-center w-100 ${
                  openDropdown === "advanceFilter" ? "show" : ""
                }`}
                type="button"
                aria-expanded={openDropdown === "advanceFilter"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDropdown("advanceFilter");
                }}
              >
                Advance Filter
              </button>
              <ul
                className={`dropdown-menu full-dropdown shadow ${
                  openDropdown === "advanceFilter" ? "show" : ""
                }`}
              >
                {filterLoading ? (
                  <li className="d-flex justify-content-center align-items-center p-4">
                    <LoadingScreen />
                  </li>
                ) : !jobsFilterList ? (
                  <li className="d-flex justify-content-center align-items-center p-4">
                    No Data Found
                  </li>
                ) : (
                  Object.keys(jobsFilterList).map((filterKey) => (
                    <li
                      className="d-flex flex-column row-gap-3"
                      key={filterKey}
                    >
                      <span className="px-3 py-2 text-sm font-semibold text-grey-800 rounded-md bg-primary-50">
                        {filterKey}
                      </span>
                      <ul className="list-unstyled d-flex flex-column row-gap-2 max-h-350 overflow-y-auto">
                        {jobsFilterList[filterKey].map(
                          (filter, filterIndex) => (
                            <li
                              className="form-check ps-4"
                              key={`${filter}-${filterIndex}`}
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name={filterKey}
                                id={`${filterKey}${SPLIT_CHARACTER}${filter}`}
                                value={`${filterKey}${SPLIT_CHARACTER}${filter}`}
                                checked={filterData.filters.includes(
                                  `${filterKey}${SPLIT_CHARACTER}${filter}`
                                )}
                                onChange={handleChangeFilter}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={filterKey}
                              >
                                {filter}
                              </label>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="search-items d-flex align-items-center gap-2">
              <button
                className="btn btn-secondary btn-lg ml-3"
                onClick={onResetFilterJobs}
              >
                Reset
              </button>

              <button className="btn btn-primary btn-lg" onClick={onFilterJobs}>
                Find Job
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-lg-10 pt-6 pb-4">
        <div className="container-xl">
          {appliedFilters.length > 0 && (
            <div className="row justify-content-center align-items-center gx-xl-5">
              <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
                <div className="d-flex flex-sm-nowrap flex-wrap justify-content-between align-items-start gap-3">
                  <div>
                    <ul className="list-unstyled mb-0 d-inline-flex flex-wrap align-items-center gap-1">
                      {appliedFilters.map((badge, idx) => (
                        <li key={idx.toString()}>
                          <span className="pl-3 py-1 pr-1 text-xs text-grey-500 rounded-pill bg-grey-100 d-flex align-items-center gap-2">
                            {badge.label}
                            <button
                              className="w-5 h-5 border-0 d-flex justify-content-center align-items-center rounded-circle bg-white"
                              onClick={() =>
                                handleRemoveAppliedFilter(
                                  badge.type,
                                  badge.value ?? badge.label
                                )
                              }
                            >
                              <i className="bx bx-x"></i>
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Outlet
        context={{
          loading: loading,
          jobsList: jobsList,
          totalJobsCount: totalJobsCount,
        }}
      />

      <section className="py-20 bg-grey-100">
        <div className="container-xl">
          <div className="row justify-content-center align-items-center gx-xl-5 mb-5">
            <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
              <div className="d-flex justify-content-between align-items-start gap-3">
                <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                  Testimonials
                </h2>
              </div>
            </div>
          </div>

          <Slider {...settings} className="student-testimonial">
            {jobsTestimonialListLoading ? (
              <LoadingScreen />
            ) : (
              jobsTestimonialList.length > 0 &&
              jobsTestimonialList.map((item, index) => (
                <div className="px-2 h-100" key={`${item.gid}-${index}`}>
                  <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
                    {/* <div className="mb-3">
                      <i className="bx bxs-star bx-sm text-secondary"></i>
                      <i className="bx bxs-star bx-sm text-secondary"></i>
                      <i className="bx bxs-star bx-sm text-secondary"></i>
                      <i className="bx bxs-star bx-sm text-secondary"></i>
                      <i className="bx bxs-star bx-sm text-secondary"></i>
                    </div> */}

                    <p className="mb-4 line-clamp-4">{item.feedback}</p>

                    <div className="d-flex align-items-center mt-auto">
                      <img
                        src={
                          item.avatar
                            ? item.avatar
                            : "/assets/images/placeholders/profile-image-placeholder.svg"
                        }
                        alt="Robert Fox"
                        className="rounded-circle me-3 w-12 h-12"
                      />
                      <div>
                        <h6 className="mb-0 fw-semibold">{item.name}</h6>
                        <small className="text-muted">{item.designation}</small>
                      </div>
                    </div>

                    {/* <div className="position-absolute bottom-0 end-0 pe-4 pb-3 text-secondary opacity-25 fs-1">
                      <i className="bx bxs-quote-single-right"></i>
                    </div> */}
                  </div>
                </div>
              ))
            )}
          </Slider>
        </div>
      </section>
    </main>
  );
}
